export interface CustomerHeaderSummary {
  customerName: string;
  websiteCount: number;
  accountManager: string;
  status: string;
  reportingPeriod: string;
}

export interface CustomerHealthBucket {
  label: string;
  value: number;
}

export type CustomerHealthChipColor = 'success' | 'warning' | 'error' | 'default';

export function getCustomerHealthChipColor(health: string): CustomerHealthChipColor {
  if (health === 'Critical') return 'error';
  if (health === 'Needs Attention') return 'warning';
  if (health === 'Healthy' || health === 'Good') return 'success';
  return 'default';
}

export function getLatestCustomerRefreshTimestamp(
  sites: Array<{ last_refreshed_at?: string }>,
): string | null {
  const timestamps = sites
    .map((site) => site.last_refreshed_at)
    .filter((value): value is string => Boolean(value) && !Number.isNaN(Date.parse(value)))
    .sort((left, right) => Date.parse(right) - Date.parse(left));

  return timestamps[0] ?? null;
}

export interface CustomerKpiCard {
  label: string;
  value: string;
  helperText: string;
}

export interface CustomerWebsiteSummaryRow {
  website: string;
  seoScore: number;
  health: string;
  criticalIssues: number;
  openIssues: number;
  organicTraffic: string;
  searchClicks: string;
  trafficChange: string;
  top10Keywords: string;
  lastCrawl: string;
  actions: string;
}

export interface CustomerIssueRow {
  issue: string;
  severity: string;
  websitesAffected: number;
  pagesAffected: number;
  firstDetected: string;
  lastDetected: string;
  status: string;
  action: string;
}

export interface CustomerActivityItem {
  title: string;
  subtitle: string;
  timestamp: string;
}

export interface CustomerTrendPoint {
  name: string;
  score: number;
  traffic: number;
  searchPerformance: number;
  ranking: number;
}

export interface CustomerRecentChange {
  title: string;
  changeType: string;
  location: string;
  previousValue: string;
  newValue: string;
  detectedAt: string;
  severity: string;
  action: string;
}

export interface CustomerPerformanceHighlight {
  seoScore: string;
  organicTraffic: string;
  searchClicks: string;
  searchImpressions: string;
  averagePosition: string;
  top10Keywords: string;
}

export interface CustomerReportSummary {
  title: string;
  period: string;
  status: string;
  generatedAt: string;
  deliveredAt: string;
  recipient: string;
  actionLabel: string;
}

export function getCustomerDashboardEmptyStateMessage(args: {
  hasLiveData?: boolean;
  selectedPage?: { id: number } | null;
}): string {
  if (!args.hasLiveData) {
    return args.selectedPage ? 'No page data available for this period.' : 'No customer data available for this period.';
  }

  return args.selectedPage ? 'No page data available for this period.' : 'No customer data available for this period.';
}

interface BuildCustomerHeaderSummaryArgs {
  site: {
    id: number;
    name: string;
    url: string;
    customerEmail?: string | null;
    isActive?: boolean | null;
    organizationId?: number | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
  siteCount: number;
  healthScore: number;
  selectedPage: { id: number } | null;
  lastRefreshedAt: string | null;
}

interface BuildCustomerHealthBreakdownArgs {
  healthy: number;
  good: number;
  issues: number;
  critical: number;
}

interface BuildCustomerKpiCardsArgs {
  healthScore: number;
  criticalIssues: number;
  openIssues: number;
  organicTraffic: string | number;
  searchClicks: string | number;
  searchImpressions: string | number;
  averagePosition: string | number;
  trackedKeywords: number;
}

export function buildCustomerHeaderSummary(args: BuildCustomerHeaderSummaryArgs): CustomerHeaderSummary {
  const siteName = args.site?.name ?? 'Customer Overview';
  const accountManager = args.site?.customerEmail ?? 'No account owner linked';
  const isHealthy = args.healthScore >= 80;

  return {
    customerName: siteName,
    websiteCount: args.siteCount,
    accountManager,
    status: isHealthy ? 'Monitoring' : 'Needs attention',
    reportingPeriod: args.selectedPage ? 'Page snapshot' : 'Last 30 days',
  };
}

export function buildCustomerHealthBreakdown(args: BuildCustomerHealthBreakdownArgs): CustomerHealthBucket[] {
  return [
    { label: 'Healthy', value: args.healthy },
    { label: 'Good', value: args.good },
    { label: 'Issues', value: args.issues },
    { label: 'Critical', value: args.critical },
  ];
}

export function buildCustomerKpiCards(args: BuildCustomerKpiCardsArgs): CustomerKpiCard[] {
  return [
    {
      label: 'SEO Score',
      value: `${Math.round(args.healthScore)}`,
      helperText: 'Composite health score',
    },
    {
      label: 'Critical Issues',
      value: `${args.criticalIssues}`,
      helperText: 'High-severity issues needing action',
    },
    {
      label: 'Open Issues',
      value: `${args.openIssues}`,
      helperText: 'Active issues across the account',
    },
    {
      label: 'Organic Traffic',
      value: typeof args.organicTraffic === 'number' ? `${args.organicTraffic}` : args.organicTraffic,
      helperText: 'Estimated visits from search',
    },
    {
      label: 'Search Clicks',
      value: typeof args.searchClicks === 'number' ? `${args.searchClicks}` : args.searchClicks,
      helperText: 'Clicks captured from search console',
    },
    {
      label: 'Search Impressions',
      value: typeof args.searchImpressions === 'number' ? `${args.searchImpressions}` : args.searchImpressions,
      helperText: 'Visible search impressions',
    },
    {
      label: 'Avg. Position',
      value: typeof args.averagePosition === 'number' ? `${args.averagePosition.toFixed(1)}` : args.averagePosition,
      helperText: 'Average ranking position',
    },
    {
      label: 'Tracked Keywords',
      value: `${args.trackedKeywords}`,
      helperText: 'Keywords monitored in the report',
    },
  ];
}

export function buildCustomerWebsiteSummaryRows(
  sites: Array<{ id: number; name?: string; url?: string; customerEmail?: string | null }>,
  seoScore: number,
  health: string,
  criticalIssues: number,
  openIssues: number,
  organicTraffic: string,
  trafficChange: string,
  top10Keywords: string,
  lastCrawl: string,
  metricSnapshot?: {
    organicTraffic?: string | number;
    searchClicks?: string | number;
    searchImpressions?: string | number;
    averagePosition?: string | number;
    trafficChange?: string | number;
    top10Keywords?: string | number;
  },
): CustomerWebsiteSummaryRow[] {
  return sites.map((site) => ({
    website: site.name || site.url || `Site #${site.id}`,
    seoScore: Math.round(seoScore),
    health,
    criticalIssues,
    openIssues,
    organicTraffic: typeof metricSnapshot?.organicTraffic === 'number' ? `${metricSnapshot.organicTraffic}` : (metricSnapshot?.organicTraffic ?? organicTraffic),
    searchClicks: typeof metricSnapshot?.searchClicks === 'number' ? `${metricSnapshot.searchClicks}` : (metricSnapshot?.searchClicks ?? 'N/A'),
    trafficChange: typeof metricSnapshot?.trafficChange === 'number' ? `${metricSnapshot.trafficChange}` : (metricSnapshot?.trafficChange ?? trafficChange),
    top10Keywords: typeof metricSnapshot?.top10Keywords === 'number' ? `${metricSnapshot.top10Keywords}` : (metricSnapshot?.top10Keywords ?? top10Keywords),
    lastCrawl,
    actions: 'Open dashboard',
  }));
}

function formatIssueDate(value?: string): string {
  if (!value) {
    return 'Today';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Today';
  }

  return parsed.toLocaleDateString();
}

export function buildCustomerIssueRows(
  issues: Array<{
    severity?: string;
    title?: string;
    status?: string;
    metric?: string;
    detected_at?: string;
    page_id?: number;
    current_value?: number;
    baseline?: number;
  }>
): CustomerIssueRow[] {
  return issues.map((issue) => ({
    issue: issue.title || issue.metric || 'Issue detected',
    severity: issue.severity || 'medium',
    websitesAffected: 1,
    pagesAffected: issue.page_id ? 1 : 2,
    firstDetected: formatIssueDate(issue.detected_at),
    lastDetected: formatIssueDate(issue.detected_at),
    status: issue.status || 'active',
    action: 'Review',
  }));
}

export function buildCustomerActivityItems(
  issues: Array<{ metric?: string; severity?: string; status?: string; detected_at?: string }>,
  recommendations: Array<{ title?: string; recommendation_type?: string; priority?: string }>
): CustomerActivityItem[] {
  return [
    ...issues.slice(0, 3).map((item) => ({
      title: `Issue detected: ${item.metric || 'Issue'}`,
      subtitle: `${item.severity || 'medium'} • ${item.status || 'active'}`,
      timestamp: item.detected_at ? formatIssueDate(item.detected_at) : 'recently detected',
    })),
    ...recommendations.slice(0, 2).map((item) => ({
      title: item.title || 'Recommendation ready',
      subtitle: item.recommendation_type || item.priority || 'Review candidate',
      timestamp: item.priority || 'actionable',
    })),
  ].slice(0, 4);
}

export function buildCustomerRecentChanges(
  issues: Array<{ metric?: string; severity?: string; status?: string; detected_at?: string }> = [],
  recommendations: Array<{ title?: string; recommendation_type?: string; priority?: string }> = []
): CustomerRecentChange[] {
  return [
    ...issues.slice(0, 2).map((item) => ({
      title: `${item.metric || 'Detected issue'} updated`,
      changeType: item.metric || 'Issue detected',
      location: 'Primary landing page',
      previousValue: 'Prior signal',
      newValue: item.status || 'Active',
      detectedAt: item.detected_at ? formatIssueDate(item.detected_at) : 'Recently',
      severity: item.severity || 'medium',
      action: 'Review details',
    })),
    ...recommendations.slice(0, 2).map((item) => ({
      title: item.title || 'Recommendation ready',
      changeType: item.recommendation_type || 'Recommendation',
      location: 'Customer site',
      previousValue: 'Pending',
      newValue: item.priority || 'High',
      detectedAt: 'New',
      severity: item.priority || 'medium',
      action: 'Follow up',
    })),
  ].slice(0, 4);
}

export function buildCustomerPerformanceHighlights(metricSnapshot?: {
  organicTraffic?: string | number;
  searchClicks?: string | number;
  searchImpressions?: string | number;
  averagePosition?: string | number;
  top10Keywords?: string | number;
  seoScore?: string | number;
}): CustomerPerformanceHighlight {
  return {
    seoScore: typeof metricSnapshot?.seoScore === 'number' ? `${metricSnapshot.seoScore}` : (typeof metricSnapshot?.seoScore === 'string' ? metricSnapshot.seoScore : '82'),
    organicTraffic: typeof metricSnapshot?.organicTraffic === 'number' ? `${metricSnapshot.organicTraffic}` : (metricSnapshot?.organicTraffic ?? 'N/A'),
    searchClicks: typeof metricSnapshot?.searchClicks === 'number' ? `${metricSnapshot.searchClicks}` : (metricSnapshot?.searchClicks ?? 'N/A'),
    searchImpressions: typeof metricSnapshot?.searchImpressions === 'number' ? `${metricSnapshot.searchImpressions}` : (metricSnapshot?.searchImpressions ?? 'N/A'),
    averagePosition: typeof metricSnapshot?.averagePosition === 'number' ? `${metricSnapshot.averagePosition.toFixed(1)}` : (metricSnapshot?.averagePosition ?? 'N/A'),
    top10Keywords: typeof metricSnapshot?.top10Keywords === 'number' ? `${metricSnapshot.top10Keywords}` : (metricSnapshot?.top10Keywords ?? 'N/A'),
  };
}

export function buildCustomerReports(args: {
  selectedPage?: { id: number } | null;
  recommendationsCount?: number;
  hasLiveData?: boolean;
  generatedAt?: string | null;
  deliveredAt?: string | null;
}): CustomerReportSummary {
  const periodLabel = args.selectedPage ? 'Page snapshot' : 'Last 30 days';
  const generatedAt = args.generatedAt ? new Date(args.generatedAt).toLocaleDateString() : 'Today';
  const deliveredAt = args.deliveredAt ? new Date(args.deliveredAt).toLocaleDateString() : 'Pending delivery';
  const actionLabel = args.hasLiveData ? 'View report' : 'Generate report';

  return {
    title: args.selectedPage ? 'Page performance report' : 'Customer SEO report',
    period: `${periodLabel} • ${Math.max(1, (args.recommendationsCount ?? 0) + 1)} review items`,
    status: args.hasLiveData ? 'Generated' : 'Draft',
    generatedAt,
    deliveredAt,
    recipient: args.selectedPage ? 'Page owner' : 'Customer team',
    actionLabel,
  };
}

export function buildCustomerTrendData(
  baseScore: number,
  offset: number,
  metricSnapshot?: {
    organicTraffic?: string | number;
    searchClicks?: string | number;
    searchImpressions?: string | number;
    averagePosition?: string | number;
  },
): CustomerTrendPoint[] {
  const trafficBase = typeof metricSnapshot?.organicTraffic === 'number'
    ? metricSnapshot.organicTraffic
    : typeof metricSnapshot?.organicTraffic === 'string' && metricSnapshot.organicTraffic !== 'N/A'
      ? Number.parseInt(metricSnapshot.organicTraffic.replace(/[^0-9]/g, ''), 10) || 0
      : 82;
  const rankingBase = typeof metricSnapshot?.averagePosition === 'number'
    ? metricSnapshot.averagePosition
    : typeof metricSnapshot?.averagePosition === 'string' && metricSnapshot.averagePosition !== 'N/A'
      ? Number.parseFloat(metricSnapshot.averagePosition)
      : 75;
  const searchPerformanceBase = typeof metricSnapshot?.searchClicks === 'number'
    ? metricSnapshot.searchClicks
    : typeof metricSnapshot?.searchClicks === 'string' && metricSnapshot.searchClicks !== 'N/A'
      ? Number.parseInt(metricSnapshot.searchClicks.replace(/[^0-9]/g, ''), 10) || 0
      : Math.max(40, trafficBase);

  return [
    { name: 'W1', score: Math.max(55, baseScore - 8 - offset), traffic: Math.max(40, trafficBase - 8), searchPerformance: Math.max(20, searchPerformanceBase - 12), ranking: Math.max(1, Math.round(rankingBase + 2)) },
    { name: 'W2', score: Math.max(60, baseScore - 4 - offset), traffic: Math.max(40, trafficBase - 4), searchPerformance: Math.max(20, searchPerformanceBase - 6), ranking: Math.max(1, Math.round(rankingBase + 1)) },
    { name: 'W3', score: baseScore, traffic: Math.max(40, trafficBase), searchPerformance: Math.max(20, searchPerformanceBase), ranking: Math.max(1, Math.round(rankingBase)) },
    { name: 'W4', score: Math.min(100, baseScore + 4 + offset), traffic: Math.max(40, trafficBase + 8), searchPerformance: Math.max(20, searchPerformanceBase + 8), ranking: Math.max(1, Math.round(rankingBase - 1)) },
  ];
}
